// When a delivery goes, in people's words, pure (timingModel.selftest.ts).
//
// The operator writes the time in the instruction itself ("At 08:00, email
// the report to …"): there is nothing to pick from a list. The server reads
// it at every save (services/workflows/deliveries/timing.py) and says when
// the delivery goes in `timing_text`; a time it could not read is
// `timing_problem`, and such a delivery cannot be approved until it is saved
// again and read. At the run's end a delivery with a time waits for it
// (`not_before`), and the run page offers Send now.

import type { DeliveryView, RunDeliveryRow } from "@/api/workflowDeliveries";
import type { StatusTone } from "@/components/ui/StatusBadge";

import { whenShort } from "./deliveryModel";

// The words the server uses for a zone (timing.zone_words).
const ZONE_NAMES: Record<string, string> = {
  "Europe/London": "London time",
  UTC: "UTC",
  "Etc/UTC": "UTC",
  "America/New_York": "New York time",
};

/** "London time", "UTC", "Singapore time". */
export function zoneWords(tz: string): string {
  return ZONE_NAMES[tz] ?? `${(tz.split("/").pop() ?? tz).replace(/_/g, " ")} time`;
}

/** "08:00 London time". */
export function atWords(holdUntil: string, tz: string): string {
  return `${holdUntil} ${zoneWords(tz)}`;
}

// ─── the editor ──────────────────────────────────────────────────────────

/** The instruction box's (i) and placeholder: what the operator can say, time included. */
export const INSTRUCTION_HELP = "Where the results go and how. A time in it (“at 08:00”) holds it until then; the agent works out the rest.";
export const INSTRUCTION_PLACEHOLDER = "For example: at 08:00, email the report to the board.";

type TimingView = Pick<DeliveryView, "timing_text" | "timing_problem">;

/** The line under a saved delivery: when it goes ("" while its time is unread: its problem says why). */
export function timingLine(v: Partial<TimingView>): string {
  return v.timing_problem ? "" : (v.timing_text ?? "");
}

/** The Approve button: never while its time is unread (the server refuses it). */
export function canApprove(v: Pick<DeliveryView, "approved"> & Partial<TimingView>): boolean {
  return !v.approved && !v.timing_problem;
}

const SOON = "Goes as soon as the run ends.";

/** What the editor says once a delivery is saved (the server's view of it). */
export function savedMessage(v: (Pick<DeliveryView, "approved"> & Partial<TimingView>) | undefined): { tone: "success" | "warning"; text: string } {
  if (v?.timing_problem) return { tone: "warning", text: `Saved, but not approved yet. ${v.timing_problem}` };
  if (v && !v.approved) return { tone: "warning", text: "Saved, but it still waits for your approval." };
  if (v?.timing_text && v.timing_text !== SOON) return { tone: "success", text: `Saved and approved. ${v.timing_text}` };
  return { tone: "success", text: "Saved and approved. It is carried out at the end of every run from now on." };
}

// ─── the run page ────────────────────────────────────────────────────────

type WaitRow = Pick<RunDeliveryRow, "state"> & Partial<Pick<RunDeliveryRow, "not_before" | "hold_until" | "tz" | "late" | "when">>;

/** Due, but its time of day has not come yet: the server gives it then. */
export function waitsForTime(r: WaitRow, now = Date.now()): boolean {
  if (r.state !== "due" || !r.not_before) return false;
  const t = Date.parse(r.not_before);
  return !Number.isFinite(t) || t > now;
}

function dayIn(t: number, tz: string): string {
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(t));
  } catch {
    return "";
  }
}

/** "Waits until 08:00 London time", with "tomorrow" when that is another day there. */
export function waitLine(r: WaitRow, now = Date.now()): string {
  if (!r.hold_until || !r.tz) return "Waits for its time.";
  const t = Date.parse(r.not_before ?? "");
  const tomorrow = Number.isFinite(t) && dayIn(t, r.tz) !== dayIn(now, r.tz);
  return `Waits until ${atWords(r.hold_until, r.tz)}${tomorrow ? " tomorrow" : ""}.`;
}

/** The state's word on the run page, where its time changes it. */
export function waitWord(r: WaitRow, now = Date.now()): { label: string; tone: StatusTone } | null {
  return waitsForTime(r, now) ? { label: "Waits for its time", tone: "neutral" } : null;
}

/** Before the run ends: when it will go. */
export function pendingLine(r: WaitRow): string {
  const when = whenShort(r.when ?? "success");
  if (!r.hold_until || !r.tz) return `Carried out ${when}.`;
  return `Carried out ${when}, at ${atWords(r.hold_until, r.tz)} (straight away if the run ends later).`;
}

/** It went after its time because the run ended later ("" otherwise). */
export function lateLine(r: WaitRow): string {
  return r.late && r.hold_until && r.tz ? `Late: the run ended after ${atWords(r.hold_until, r.tz)}.` : "";
}

/** The run page's button and its confirm: Send now while it waits for its time, else Send again. */
export function sendButton(r: WaitRow, who: string, first: string, now = Date.now()): { label: string; title: string; body: string } {
  if (waitsForTime(r, now)) {
    const at = r.hold_until && r.tz ? `at ${atWords(r.hold_until, r.tz)}` : "at its time";
    return { label: "Send now", title: "Send it now?", body: `${who} carries out “${first}” now, instead of ${at}.` };
  }
  return { label: "Send again", title: "Send this again?", body: `${who} carries out “${first}” again for this run, as a new task. Whatever it sends goes out again.` };
}
